/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * useDiscontinueProduct — the preview/confirm state machine behind the
 * Discontinue action, lifted out of the product lists so membership and
 * programming surfaces share one behaviour instead of two copies that drift.
 *
 * Discontinuing is the deliberate wind-down: it stops new sales AND cancels
 * every active subscription at the end of the member's paid period. Because it
 * ends things people are paying for, the confirm never guesses — it asks the
 * server for a dry run first and puts that real number in front of the owner.
 *
 * The one exception is a product that cannot have subscriptions at all (a free
 * or one-time-purchase product): its active-subscription count is zero by
 * construction, so the round trip is skipped and the dialog opens immediately.
 * That is a latency shortcut only — the server still re-derives the truth on
 * confirm, and nothing the client believes is trusted.
 *
 * The wind-down can also half-succeed: when the server cannot reach the Stripe
 * account a product is sold through it delists the product and cancels nothing.
 * Both the confirm and the result must say so, or the owner is told their
 * members stopped paying while Stripe carries on billing them.
 */

import { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  discontinueProduct,
  notifyActions,
  NOTIFICATION_TYPE,
  isDiscontinuePreview,
  type DiscontinueProductExecutedResult,
} from "@lemonadetraining/web-shared";
import { useUserAccess } from "./useUserAccess";
import { refusalCommitmentCount, refusalReason } from "../lib/callableError";

/**
 * Shown instead of a raw `permission-denied` so a coach who reaches this path
 * (stale claims, an open tab from before a role change) is told who CAN do it
 * rather than being handed a server error code.
 */
const PERMISSION_DENIED_MESSAGE = "Only the box admin or product owner can discontinue a product.";

/** The callable transport prefixes HttpsError codes with "functions/". */
const errorCode = (error: unknown): string =>
  ((error as { code?: string } | null)?.code || "").replace(/^functions\//, "");

/**
 * A subscription-priced product with no price cannot have its subscriptions
 * listed safely, so the server refuses the whole call. Its own wording is
 * internal ("refusing to enumerate subscriptions") and tells a gym owner
 * nothing they can act on, so it never reaches them.
 */
const pricingIncompleteMessage = (noun: string) =>
  `This product is sold as a subscription but has no price attached, so we can't safely end the ${noun}s people are paying for. Nothing has been changed. Please fix the product's pricing, or contact support if it already looks right.`;

/**
 * The server refuses to act on a product whose recorded Stripe account does not
 * match the gym or owner it belongs to, because acting on it could reach
 * someone else's account. Nothing about this is the owner's to repair, so the
 * copy asks for support rather than sending them into the pricing editor.
 */
const ACCOUNT_MISMATCH_MESSAGE =
  "This product's payment details don't match the gym they belong to, so we've stopped rather than risk changing the wrong thing. Nothing has been changed. Please contact support and we'll sort it out.";

/**
 * The server refuses to end a product for everyone while members are on a
 * commitment (LT-1998), on the dry run and on the confirmed call, before
 * anything is archived or cancelled.
 */
export interface CommitmentRefusal {
  id: string;
  name: string;
  /** How many members are on a commitment; null when the server did not say. */
  count: number | null;
}

const isCommitmentRefusal = (error: unknown): boolean =>
  errorCode(error) === "failed-precondition" && refusalReason(error) === "has_commitment_members";

export const commitmentRefusalMessage = (count: number | null): string =>
  `${
    count === null ? "Some members are" : count === 1 ? "1 member is" : `${count} members are`
  } on a commitment. Ending for everyone isn't available yet while they are; archive it instead so nobody new joins.`;

/**
 * A confirmed run that found a commitment only after taking the product off
 * sale (a checkout already open) stops there: off sale like Archive, nobody's
 * plan ended. Running it again would only be refused, so no retry is offered.
 */
export const OFF_SALE_RACE_MESSAGE =
  "It's now off sale, so nobody new can join. Nobody's plan was ended, because someone joined on a commitment while it was being ended. Everyone keeps their current plan.";

const isOffSaleRace = (error: unknown): boolean =>
  errorCode(error) === "aborted" && refusalReason(error) === "commitment_members_after_off_sale";

/**
 * Server rejections that happen BEFORE anything is mutated, in copy an owner
 * can act on. Null for anything else, which lets each caller decide what an
 * unrecognised failure means for the state of the product.
 */
const preMutationMessage = (error: unknown, noun: string): string | null => {
  switch (errorCode(error)) {
    case "permission-denied":
      return PERMISSION_DENIED_MESSAGE;
    case "failed-precondition":
      switch (refusalReason(error)) {
        case "account-mismatch":
          return ACCOUNT_MISMATCH_MESSAGE;
        case "has_commitment_members":
          return commitmentRefusalMessage(refusalCommitmentCount(error));
        default:
          return pricingIncompleteMessage(noun);
      }
    default:
      return null;
  }
};

/**
 * A confirmed wind-down archives the product FIRST and then cancels
 * subscriptions one at a time, so a failure part-way through leaves the product
 * off sale and an arbitrary prefix of members already cancelled. Reporting only
 * "failed" would tell the owner nothing happened, which is untrue and would
 * stop them from finishing.
 *
 * Every claim about what changed is hedged, because from here the two are
 * indistinguishable: a request that died in the browser changed nothing, and a
 * cancel loop that stopped half way changed a great deal. Only the instruction
 * is stated flatly — re-running IS safe whichever happened, since cancelling an
 * already-cancelling subscription at period end changes nothing.
 */
const partialWindDownMessage = (name: string, noun: string) =>
  `We couldn't confirm that discontinuing "${name}" finished. It may already be off sale, and some ${noun}s may already have been cancelled. Running Discontinue again is safe and will finish the job.`;

/**
 * The server delists the product locally even when it cannot reach the Stripe
 * account it is sold through, and then nothing is cancelled. Reporting that as a
 * success would tell the owner their members are done paying while Stripe keeps
 * billing them, so it is reported as a warning that names the one action left.
 */
const stripeUnreachableMessage = (name: string, noun: string) =>
  `"${name}" was taken off sale, so nobody new can buy it. We couldn't reach the Stripe account it is sold through, so existing ${noun}s have not been cancelled and those members are still being billed. Reconnect Stripe and run Discontinue again to end them.`;

interface DiscontinueAccessState {
  user: {
    user: { uid?: string } | null;
  };
}

/**
 * Mirrors the `discontinueProduct` server predicate so coach-level viewers are
 * not shown a button that can only fail: allowed for a platform admin, for the
 * product's own owner, or for an admin of the product's box. Note there is no
 * "owner" boxRole in this codebase — a box creator is stored as
 * `boxRole: "admin"`, so the box arm is that single check.
 *
 * This is a UI affordance ONLY. The Cloud Function is the authority and
 * re-checks every one of these; nothing here may be treated as a security
 * boundary. It fails closed while claims are still resolving.
 */
export const useCanDiscontinue = (): ((product: { ownerUID?: string | null }) => boolean) => {
  const { isAdmin, isBoxAdmin, isLoading } = useUserAccess();
  const uid = useSelector((state: DiscontinueAccessState) => state.user.user?.uid);

  return (product: { ownerUID?: string | null }) => {
    if (isLoading) return false;
    if (isAdmin) return true;
    if (uid && product.ownerUID === uid) return true;
    return isBoxAdmin;
  };
};

/** The product currently queued for confirmation, with its dry-run count. */
export interface DiscontinueTarget {
  id: string;
  name: string;
  /** Null when the server could not read the count because Stripe was unreachable. */
  activeSubscriptionCount: number | null;
  /** False when a confirmed run would delist the product without unsubscribing anybody. */
  stripeWoundDown: boolean;
}

interface UseDiscontinueProductArgs {
  /** Product collection the UIDs belong to — see constants/ProductConst TYPE. */
  productType: string;
  /** The owning box, when the products belong to one (absent for independent coaches). */
  boxID?: string | null;
  /** What one of these products is called in copy, e.g. "membership". */
  noun: string;
  /** Called after a successful discontinue, for surfaces that need to re-read. */
  onCompleted?: () => void;
  /** The caller shows `commitmentRefusal` itself; otherwise it is reported as an error. */
  showsCommitmentRefusal?: boolean;
}

interface UseDiscontinueProductResult {
  /** Non-null while the confirm dialog should be open. */
  target: DiscontinueTarget | null;
  /** True while either the preview or the confirmed run is in flight. */
  busy: boolean;
  /** The product whose preview is currently loading, for a per-row spinner. */
  pendingID: string | null;
  /**
   * Whether ONE product's Discontinue control should be disabled. Deliberately
   * per-product: a list-wide flag greys out every row for one owner's dry run,
   * which on a long product list reads as a page that has broken. A confirmed
   * wind-down still locks the whole list, since that one is mutating.
   */
  isRowBusy: (productID: string) => boolean;
  requestDiscontinue: (product: {
    id: string;
    name: string;
    /** `pricing.type` — anything other than "subscription" cannot have active subs. */
    pricingType?: string;
  }) => Promise<void>;
  confirmDiscontinue: () => Promise<void>;
  cancel: () => void;
  /** Set when the server refused because members are on a commitment. */
  commitmentRefusal: CommitmentRefusal | null;
  clearCommitmentRefusal: () => void;
}

export const useDiscontinueProduct = ({
  productType,
  boxID,
  noun,
  onCompleted,
  showsCommitmentRefusal = false,
}: UseDiscontinueProductArgs): UseDiscontinueProductResult => {
  const dispatch = useDispatch();
  const [target, setTarget] = useState<DiscontinueTarget | null>(null);
  const [pendingID, setPendingID] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [commitmentRefusal, setCommitmentRefusal] = useState<CommitmentRefusal | null>(null);
  /*
   * Which dry run is still wanted. Rows are individually clickable while
   * another row previews, so a slower earlier response must never open the
   * confirm for a product the owner has already moved on from.
   */
  const latestPreview = useRef(0);

  const notifyError = (message: string) =>
    dispatch(
      notifyActions.sendNotification({
        severity: NOTIFICATION_TYPE.error,
        title: "Error",
        message,
      })
    );

  const requestDiscontinue = async (product: {
    id: string;
    name: string;
    pricingType?: string;
  }) => {
    const requestID = (latestPreview.current += 1);

    // A product that isn't sold as a subscription has no active subscriptions to
    // end, so there is nothing for a dry run to discover.
    if (product.pricingType !== undefined && product.pricingType !== "subscription") {
      setTarget({
        id: product.id,
        name: product.name,
        activeSubscriptionCount: 0,
        stripeWoundDown: true,
      });
      return;
    }

    try {
      setPendingID(product.id);
      const response = await discontinueProduct(product.id, productType, boxID || undefined);
      if (latestPreview.current !== requestID) return;
      const preview = response?.data;
      if (!isDiscontinuePreview(preview)) {
        throw new Error(`We couldn't check who this ${noun} would affect. Please try again.`);
      }
      setTarget({
        id: product.id,
        name: product.name,
        activeSubscriptionCount: preview.activeSubscriptionCount,
        stripeWoundDown: preview.stripeWoundDown !== false,
      });
    } catch (error: unknown) {
      if (latestPreview.current !== requestID) return;
      if (showsCommitmentRefusal && isCommitmentRefusal(error)) {
        setCommitmentRefusal({
          id: product.id,
          name: product.name,
          count: refusalCommitmentCount(error),
        });
        return;
      }
      // Nothing is mutated on this path, so an unrecognised failure is reported
      // as-is rather than as a half-finished wind-down.
      notifyError(
        preMutationMessage(error, noun) ||
          (error as Error)?.message ||
          `Failed to check who this ${noun} would affect`
      );
    } finally {
      if (latestPreview.current === requestID) setPendingID(null);
    }
  };

  const confirmDiscontinue = async () => {
    if (!target) return;

    const { id, name } = target;
    try {
      setConfirming(true);
      const response = await discontinueProduct(id, productType, boxID || undefined, true);
      const result = response?.data as Partial<DiscontinueProductExecutedResult> | undefined;
      const cancelledCount = result?.cancelledCount;
      // A preview shape here means the run did NOT execute; reporting "0 ended"
      // would tell the owner something untrue about people's subscriptions.
      if (isDiscontinuePreview(result) || typeof cancelledCount !== "number") {
        throw new Error(
          `We couldn't confirm that "${name}" was discontinued. Please refresh and check it.`
        );
      }
      // `cancelledCount: 0` alongside stripeWoundDown false means none were
      // attempted, not that nobody was subscribed, so it is never a success.
      const stripeWoundDown = result.stripeWoundDown !== false;
      dispatch(
        notifyActions.sendNotification(
          stripeWoundDown
            ? {
                severity: NOTIFICATION_TYPE.success,
                title: "Success",
                message:
                  cancelledCount === 0
                    ? `"${name}" discontinued. It is no longer available for purchase.`
                    : `"${name}" discontinued. ${cancelledCount} ${
                        cancelledCount === 1 ? noun : `${noun}s`
                      } will end at the end of the current billing period.`,
              }
            : {
                severity: NOTIFICATION_TYPE.warning,
                title: "Members were not unsubscribed",
                message: stripeUnreachableMessage(name, noun),
              }
        )
      );
      setTarget(null);
      onCompleted?.();
    } catch (error: unknown) {
      if (isOffSaleRace(error)) {
        dispatch(
          notifyActions.sendNotification({
            severity: NOTIFICATION_TYPE.warning,
            title: "Nobody's plan was ended",
            message: OFF_SALE_RACE_MESSAGE,
          })
        );
        setTarget(null);
        onCompleted?.();
        return;
      }
      if (showsCommitmentRefusal && isCommitmentRefusal(error)) {
        setTarget(null);
        setCommitmentRefusal({ id, name, count: refusalCommitmentCount(error) });
        return;
      }
      // Past this point the product is already off sale, so only the failures
      // the server raises BEFORE it mutates may claim nothing has changed.
      notifyError(preMutationMessage(error, noun) || partialWindDownMessage(name, noun));
    } finally {
      setConfirming(false);
    }
  };

  const cancel = () => setTarget(null);

  return {
    target,
    busy: confirming || pendingID !== null,
    pendingID,
    isRowBusy: (productID: string) => confirming || pendingID === productID,
    requestDiscontinue,
    confirmDiscontinue,
    cancel,
    commitmentRefusal,
    clearCommitmentRefusal: () => setCommitmentRefusal(null),
  };
};
